import {
  Autocomplete,
  Box,
  TextField,
  Tooltip,
  createFilterOptions,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import { FaEdit } from 'react-icons/fa'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon'

interface DentistOption {
  title: string
  id: string
  // MUI's createFilterOptions adds `inputValue` dynamically
  inputValue?: string
}

interface EditableFieldProps {
  value: string
  label: string
  onSave: (value: string, close: () => void) => void
  dentistList?: Array<{ name: string; id: string }>
  caseDetails?: {
    name: string
    dentist?: { name: string; id: string }
    case_type?: string
    role: string
  }
}

interface DentistOptionItemProps {
  option: DentistOption
  liProps: React.HTMLAttributes<HTMLLIElement>
}

const DentistOptionItem: React.FC<DentistOptionItemProps> = ({
  option,
  liProps,
}) => {
  return <li {...liProps}>{option.title}</li>
}

const EditableField: React.FC<EditableFieldProps> = ({
  value,
  label,
  onSave,
  dentistList,
  caseDetails,
}) => {
  const [fieldValue, setFieldValue] = useState('')
  const theme = useTheme()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const toggleEditMode = () => setIsDialogOpen((prev) => !prev)

  enum LABEL {
    PROJECT = 'Project',
  }

  useEffect(() => {
    setFieldValue(value)
  }, [value])

  const dentistOptions: DentistOption[] =
    dentistList?.map((item) => ({
      title: item.name,
      id: item.id,
    })) || []

  const filter = createFilterOptions<DentistOption>()

  const getLabelForOption = (option: DentistOption | string): string => {
    if (typeof option === 'string') {
      return option
    }

    if ('inputValue' in option && option.inputValue) {
      return option.inputValue
    }

    return option.title
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: '0.875rem',
      }}
    >
      <Box
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '0.75rem',
          fontWeight: 700,
          lineHeight: '0.875rem',
        }}
      >{`${label}:`}</Box>

      {caseDetails?.case_type !== 'auto' && (
        <Tooltip title={value}>
          <Box
            component="span"
            sx={{
              color: theme.palette.text.secondary,
              px: 1,
              fontSize: '0.75rem',
              lineHeight: '0.875rem',
              maxWidth: '135px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </Box>
        </Tooltip>
      )}

      <Dialog
        triggerElement={
          <Box
            display="flex"
            sx={{ cursor: 'pointer' }}
            onClick={() => setIsDialogOpen(true)}
          >
            {caseDetails?.role !== 'editor' && (
              <Icon
                icon={FaEdit}
                size={14}
                color={theme.palette.primary.main}
              />
            )}
          </Box>
        }
        paperStyles={{ width: '800px' }}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={`Edit ${label} Name`}
        content={
          label === LABEL.PROJECT ? (
            <TextField
              label={`Enter ${label} Name`}
              value={caseDetails?.case_type === 'auto' ? '' : fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              fullWidth
              sx={{
                '& .MuiInputBase-input ': {
                  paddingY: '8px',
                },
                '& .MuiFormLabel-root': {
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset, &:hover fieldset': {
                    borderColor: theme.palette.action.disabled,
                    borderWidth: '1px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputLabel-root': {
                  top: '-8px',
                },
                '& .MuiInputLabel-shrink': {
                  top: '0px',
                },
              }}
            />
          ) : (
            <Autocomplete<DentistOption, false, false, true>
              value={value}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  setFieldValue(newValue)
                } else if (newValue && 'inputValue' in newValue) {
                  setFieldValue(newValue.inputValue || '')
                } else {
                  setFieldValue(newValue?.title || '')
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params)
                const { inputValue } = params

                if (
                  inputValue !== '' &&
                  !options.some((option) => option.title === inputValue)
                ) {
                  filtered.push({
                    inputValue,
                    title: `Add "${inputValue}"`,
                    id: '',
                  })
                }

                return filtered
              }}
              options={dentistOptions}
              getOptionLabel={(option) => getLabelForOption(option)}
              renderOption={(props, option) => (
                <DentistOptionItem option={option} liProps={props} />
              )}
              freeSolo
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      paddingY: '8px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '0px !important',
                    },
                    '& .MuiFormLabel-root': {
                      '&.Mui-focused': {
                        color: theme.palette.primary.main,
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset, &:hover fieldset': {
                        borderColor: theme.palette.action.disabled,
                        borderWidth: '1px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      top: '-8px',
                    },
                    '& .MuiInputLabel-shrink': {
                      top: '0px',
                    },
                  }}
                  {...params}
                  label="Edit Dentist Name"
                />
              )}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'window',
                      },
                    },
                  ],
                },
                paper: {
                  sx: {
                    backgroundColor: theme.palette.background.disableTab,
                    fontSize: '16px',
                    fontWeight: '400',
                    paddingY: '6px ',
                  },
                },
              }}
            />
          )
        }
        actionButtons={
          <>
            <Button
              variant="outlined"
              label="CANCEL"
              onClick={toggleEditMode}
            />
            <Button
              label="SAVE"
              disabled={!fieldValue}
              onClick={() => onSave(fieldValue, toggleEditMode)}
            />
          </>
        }
      />
    </Box>
  )
}

export default EditableField
